import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FormControl,
  FormLabel,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
  createActivitySchema,
  type ActivityResponseType,
} from '../activity-card/activity-dialog/schema';
import { BaseDialog } from '@/components/shared/base-dialog';
import { useCreatePlanActivity } from '@/data/idp/plans/activities/create';
import { IdpEvent } from '@/constants/analytics';
import {
  buildIdpActivityProperties,
  useIdpPlan,
} from '@/packages/idp/utils/idp-analytics';
import { track } from '@/utils/analytics';

interface CreateActivityDialogProps {
  onClose: () => void;
  planId: string;
}

export const CreateActivityDialog = ({
  onClose,
  planId,
}: CreateActivityDialogProps) => {
  const { mutate: createPlanActivity } = useCreatePlanActivity(planId);
  const plan = useIdpPlan(planId);
  const { control, formState, handleSubmit } = useForm({
    resolver: zodResolver(createActivitySchema),
    defaultValues: {
      description: '',
      title: '',
    },
  });

  const handleSave = (data: ActivityResponseType) => {
    createPlanActivity(data, {
      onSuccess: (activity) => {
        track(
          IdpEvent.ActivityCreated,
          buildIdpActivityProperties(plan, activity)
        );
        onClose();
      },
    });
  };

  return (
    <BaseDialog
      title="Nova atividade"
      onClose={onClose}
      actionsComponent={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            disabled={!formState.isValid}
            loading={formState.isSubmitting}
            onClick={handleSubmit(handleSave)}
            variant="contained"
          >
            Salvar
          </Button>
        </>
      }
    >
      <Controller
        name="title"
        control={control}
        render={({ field, fieldState }) => {
          return (
            <FormControl fullWidth>
              <FormLabel htmlFor="title">Nome da atividade</FormLabel>
              <TextField
                {...field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                id="title"
                placeholder="Dê um nome para a sua atividade"
                variant="outlined"
              />
            </FormControl>
          );
        }}
      />
      <Controller
        name="description"
        control={control}
        render={({ field }) => {
          return (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <FormLabel htmlFor="description">
                Descrição
                <Typography variant="body2" component="span" ml={1}>
                  Opcional
                </Typography>
              </FormLabel>
              <TextField
                {...field}
                id="description"
                multiline
                placeholder="Adicionar descrição"
                rows={4}
                variant="outlined"
              />
            </FormControl>
          );
        }}
      />
    </BaseDialog>
  );
};
